<template>
  <div class="text-center">
    <v-menu
      v-model="menu"
      :close-on-content-click="false"
      bottom
      offset-y
      @update:model-value="onMenuUpdate"
    >
      <template v-slot:activator="{ props }">
        <v-chip :color="filteredValue ? 'primary' : 'primary'" v-bind="props" label>
          {{ chipLabel }}
          <span v-if="filteredValue">&nbsp;|&nbsp;</span>
          <span class="font-weight-bold"> {{ filteredValue }}</span>
          <v-btn
            v-if="filteredValue"
            variant="text"
            size="x-small"
            icon="mdi-close"
            @click.stop="reset()"
          />
        </v-chip>
      </template>

      <v-card min-width="350" class="mt-2">
        <v-list>
          <v-list-item :title="getFilterTitle">
            <template v-slot:append>
              <v-btn variant="text" icon="mdi-close" @click="menu = false" />
            </template>
          </v-list-item>
        </v-list>

        <v-divider />

        <v-text-field
          class="mt-3 mx-6 pt-3"
          v-model="searchValue"
          @input="updateSearchValue"
          @keydown.enter="applyAndCloseFilter"
          :label="labelText"
        />

        <v-card-actions>
          <v-spacer />
          <v-btn @click="applyAndCloseFilter" color="primary" class="mr-2">
            {{ $t("page.payment.filter.applyFilter") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-menu>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted, watch } from "vue";
import { usePricesStore } from "@/catalog/stores/prices";
import { useProductClassesStore } from "@/catalog/stores/productClasses";
import { useProductTagsStore } from "@/catalog/stores/productTags";
import { useProductsStore } from "@/catalog/stores/products";
import i18n from "@/app/plugins/i18n";
import { useSnackBarStore } from "@/shared/stores/snackbar";
import type { FilterCondition } from "@/shared/types/filters";

// Define props to receive the context from parent components
const props = defineProps({
  context: {
    type: String,
    default: "price",
  },
});

const menu = ref(false);
const searchValue = ref("");

interface NameFilterContextConfig {
  filterKey: string;
  chipLabelKey: string;
  labelTextKey: string;
  filterTitleKey: string;
  minLength?: number;
  getCondition?: () => FilterCondition;
  setCondition?: (condition: FilterCondition) => void;
  searchMethod?: (opts?: object, reload?: boolean) => Promise<unknown>;
}

const contextConfig = ref<NameFilterContextConfig | null>(null);

// Set up context-specific configurations on mount
onMounted(() => {
  setupContextConfiguration();
  initializeSearchValue();
});

// Watch for changes to the context prop
watch(
  () => props.context,
  () => {
    setupContextConfiguration();
    initializeSearchValue();
  },
);

// Set up the context-specific configuration
const setupContextConfiguration = () => {
  if (props.context === "product") {
    const productsStore = useProductsStore();
    contextConfig.value = {
      filterKey: "name",
      chipLabelKey: "common.action.name",
      labelTextKey: "common.filter.nameMinCharacters",
      filterTitleKey: "common.filter.byName",
      minLength: 3,
      getCondition: () => productsStore.filterProductCondition || {},
      setCondition: (condition) => productsStore.setFilterProductCondition(condition),
      searchMethod: (opts = {}, reload = true) => productsStore.searchProducts(opts, reload),
    };
  } else if (props.context === "product-class") {
    // Configuration for product class context
    const productClassesStore = useProductClassesStore();
    contextConfig.value = {
      filterKey: "name",
      chipLabelKey: "common.action.name",
      labelTextKey: "common.action.name",
      filterTitleKey: "common.filter.byName",
      minLength: 3,
      getCondition: () => productClassesStore.filterProductClassCondition || {},
      setCondition: (condition) => productClassesStore.setFilterProductClassCondition(condition),
      searchMethod: (opts = {}, reload = true) =>
        productClassesStore.searchProductClasses(opts, reload),
    };
  } else if (props.context === "product-tag") {
    // Configuration for product tag context
    const productTagsStore = useProductTagsStore();
    contextConfig.value = {
      filterKey: "name",
      chipLabelKey: "common.action.name",
      labelTextKey: "common.action.name",
      filterTitleKey: "common.filter.byName",
      getCondition: () => productTagsStore.filterProductTagCondition || {},
      setCondition: (condition) => productTagsStore.setFilterProductTagCondition(condition),
      searchMethod: (opts = {}, reload = true) => productTagsStore.searchProductTags(opts, reload),
    };
  } else {
    // Default configuration for price context
    const pricesStore = usePricesStore();
    contextConfig.value = {
      filterKey: "nickname",
      chipLabelKey: "common.action.nickname",
      labelTextKey: "common.action.nickname",
      filterTitleKey: "common.filter.byNickname",
      getCondition: () => pricesStore.filterPriceCondition || {},
      setCondition: (condition) => pricesStore.setFilterPriceCondition(condition),
      searchMethod: (opts = {}, reload = true) => pricesStore.searchPrices(opts, reload),
    };
  }
};

// Get the current configuration
const getConfig = () =>
  contextConfig.value || {
    filterKey: "nickname",
    chipLabelKey: "common.action.nickname",
    labelTextKey: "common.action.nickname",
    filterTitleKey: "common.filter.byNickname",
  };

const translateConfigKey = (key: string) => i18n.global.t(key);

// Computed properties for UI elements
const chipLabel = computed(() => translateConfigKey(getConfig().chipLabelKey));
const labelText = computed(() => translateConfigKey(getConfig().labelTextKey));
const getFilterTitle = computed(() => translateConfigKey(getConfig().filterTitleKey));

// Get the current filter value
const filteredValue = computed(() => {
  const config = getConfig();
  const condition = config.getCondition ? config.getCondition() : {};
  return String(condition[config.filterKey] ?? "");
});

// Initialize search value based on current filter
const initializeSearchValue = () => {
  const config = getConfig();
  const condition = config.getCondition ? config.getCondition() : {};
  searchValue.value = String(condition[config.filterKey] ?? "");
};

// Update search value on input
const updateSearchValue = (event: Event) => {
  searchValue.value = (event.target as HTMLInputElement).value;
};

// Reset filter
const reset = async () => {
  searchValue.value = "";

  const config = getConfig();
  const condition = config.getCondition ? config.getCondition() : {};

  // Remove the filter key
  delete condition[config.filterKey];

  // Apply updated condition
  if (config.setCondition) {
    config.setCondition(condition);
  }

  // Perform search with updated filters. Keep product/product-group/tag filtering scoped to the table loader.
  if (config.searchMethod) {
    const tableOnlyLoading =
      props.context === "product" ||
      props.context === "product-class" ||
      props.context === "product-tag";
    await config.searchMethod({}, !tableOnlyLoading);
  }
};

// Apply the filter and update the store
const updateFilter = () => {
  const config = getConfig();
  const condition = config.getCondition ? { ...config.getCondition() } : {};
  const value = searchValue.value.trim();

  if (value && config.minLength && value.length < config.minLength) {
    useSnackBarStore().toastError({
      message: i18n.global.t("common.filter.nameMinCharactersError", [config.minLength]),
    });
    return false;
  }

  if (value) {
    condition[config.filterKey] = value;
  } else {
    delete condition[config.filterKey];
  }

  // Apply updated condition
  if (config.setCondition) {
    config.setCondition(condition);
  }

  // Perform search with updated filters. Keep product/product-group/tag filtering scoped to the table loader.
  if (config.searchMethod) {
    const tableOnlyLoading =
      props.context === "product" ||
      props.context === "product-class" ||
      props.context === "product-tag";
    config.searchMethod({}, !tableOnlyLoading);
  }
  return true;
};

// Apply filter and close menu
const applyAndCloseFilter = () => {
  if (updateFilter()) {
    menu.value = false;
  }
};

// Update search value when menu opens
const onMenuUpdate = (isOpen: boolean) => {
  if (isOpen) {
    initializeSearchValue();
  }
};
</script>
